import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { PlusCircle, Users, ShieldCheck } from 'lucide-react';
import SecurityStatusBanner from '../SecurityStatusBanner';
import { useUserTier } from '../../hooks/useUserTier';

// Tier-aware onboarding welcome card.
//   * Premium users → original CTAs (Add Investment / View Sample Portfolio)
//   * Continuity System gateway users → vault-first CTAs (Set up Vault /
//     Add Trusted Contact) so the copy matches their actual product.
const DashboardWelcome = ({ user, portfolioSummary, activeTab, setActiveTab }) => {
  const { isGatewayContinuity } = useUserTier();
  const showEmptyWelcome = portfolioSummary && portfolioSummary.total_investments === 0;
  return (
    <>
      {showEmptyWelcome && (
        <div className="mb-6 sm:mb-8">
          <Card className="portfolio-card bg-linear-to-r from-emerald-50 to-blue-50 border-emerald-200">
            <CardContent className="p-4 sm:p-8 text-center">
              <div className="space-y-3 sm:space-y-4">
                <h2 className="text-xl sm:text-2xl font-serif font-bold text-slate-900">
                  Welcome to MyInvestFolio{user?.code_name ? `, ${user.code_name}` : ''}!
                </h2>
                <p className="body-text text-sm sm:text-base max-w-2xl mx-auto">
                  {isGatewayContinuity
                    ? "Let's set up your Continuity System. Record your first asset, add a trusted contact, or upload a key document — at your own pace."
                    : 'Get started by adding your first investment, or explore the platform with our sample portfolio.'}
                </p>
                <div className="flex flex-wrap justify-center gap-3">
                  {isGatewayContinuity ? (
                    <>
                      <Button data-testid="add-first-investment-btn" onClick={() => setActiveTab('add')} className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm sm:text-base">
                        <PlusCircle className="w-4 h-4 mr-2" />Record an Asset
                      </Button>
                      <Button data-testid="add-first-contact-btn" onClick={() => setActiveTab('estate-vault')} variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-sm sm:text-base">
                        <Users className="w-4 h-4 mr-2" />Add Trusted Contact
                      </Button>
                      <Button data-testid="explore-vault-btn" onClick={() => setActiveTab('estate-vault')} variant="ghost" className="text-slate-600 hover:bg-slate-100 text-sm sm:text-base">
                        <ShieldCheck className="w-4 h-4 mr-2" />Explore your Vault
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button data-testid="add-first-investment-btn" onClick={() => setActiveTab('add')} className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm sm:text-base">
                        <PlusCircle className="w-4 h-4 mr-2" />Add Investment
                      </Button>
                      <Button data-testid="view-sample-btn" onClick={() => setActiveTab('sample')} variant="outline" className="border-purple-300 text-purple-600 hover:bg-purple-50 text-sm sm:text-base">
                        View Sample Portfolio
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      {activeTab === 'dashboard' && user && (
        <div className="mb-6">
          <SecurityStatusBanner
            user={user}
            onNavigateToSettings={(section) => {
              setActiveTab('settings');
              // If the caller asked for a specific section (e.g. 'security'
              // from the Enable 2FA button), fire a scroll event once the
              // Settings tab has mounted. SettingsTab listens for this and
              // scrolls the matching #-anchor into view. Session 262.
              if (section) {
                setTimeout(() => {
                  window.dispatchEvent(new CustomEvent('mif:scroll-settings-to', { detail: section }));
                }, 250);
              }
            }}
          />
        </div>
      )}
    </>
  );
};

export default DashboardWelcome;
